import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, AlertCircle } from 'lucide-react'
import Button from '../components/ui/Button'
import Loading from '../components/ui/Loading'
import TranscriptionViewer from '../components/transcription/TranscriptionViewer'
import { useTranscription } from '../hooks/useTranscription'

const TranscriptionPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { 
    transcription, 
    loading, 
    error, 
    isProcessing, 
    isCompleted, 
    isFailed,
    deleteTranscription,
    refetch: refreshTranscription
  } = useTranscription(id)

  const handleGoBack = () => {
    navigate('/dashboard')
  }

  const handleDelete = async () => {
    if (window.confirm('确定要删除这个转录记录吗？此操作无法撤销。')) {
      try {
        await deleteTranscription()
        navigate('/dashboard')
      } catch (error) {
        alert('删除失败：' + error.message)
      }
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loading size="lg" text="加载转录内容..." />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">加载失败</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={handleGoBack}>
            返回首页
          </Button>
        </div>
      </div>
    )
  }

  if (!transcription) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">转录记录不存在</h2>
          <p className="text-gray-600 mb-4">请检查链接是否正确</p>
          <Button onClick={handleGoBack}>
            返回首页
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 页面头部 */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={handleGoBack}
              className="mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              返回列表
            </Button>

            <Button
              variant="outline"
              onClick={handleDelete}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              删除转录
            </Button>
          </div>
        </div>

        {/* 处理中状态 */}
        {isProcessing && (
          <div className="mb-8">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <div className="flex items-center justify-center space-x-3">
                <Loading size="sm" text="" />
                <div>
                  <h3 className="text-lg font-medium text-yellow-800">
                    正在处理音频文件
                  </h3>
                  <p className="text-yellow-700">
                    转录正在进行中，预计需要几分钟时间。页面会自动刷新显示最新状态。
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 处理失败状态 */}
        {isFailed && (
          <div className="mb-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex items-center">
                <AlertCircle className="h-8 w-8 text-red-400 mr-3" />
                <div>
                  <h3 className="text-lg font-medium text-red-800">
                    转录处理失败
                  </h3>
                  <p className="text-red-700">
                    {transcription.error_message || '转录过程中出现错误，请重新上传文件'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 转录内容 */}
        {isCompleted && (
          <TranscriptionViewer 
            transcription={transcription} 
            onTranscriptionUpdate={refreshTranscription}
          />
        )}
      </div>
    </div>
  )
}

export default TranscriptionPage